import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { OnboardingProvider } from "@/hooks/useOnboarding";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { FeatureErrorBoundary } from "@/components/FeatureErrorBoundary";
import { LoadingScreen } from "@/components/LoadingScreen";
import { RouteGuard } from "./components/auth/RouteGuard";
import { FeatureRouteGuard } from "./components/subscription/FeatureRouteGuard";
import { Sentry } from "@/lib/sentry";
import { AppErrorFallback } from "@/components/AppErrorFallback";

// ── Public / shell pages (loaded eagerly — needed on first paint) ──────────
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import AppLayout from "@/components/layouts/AppLayout";
import Settings from "./pages/Settings";
import { ROUTES } from '@/lib/routes';

// ── All /app/* routes (lazy — split away from marketing bundle) ────────────

const UploadHistory = lazy(() => import("./pages/UploadHistory"));
const Players = lazy(() => import("./pages/Players"));
const LoadMonitoring = lazy(() => import("./pages/LoadMonitoring"));
const SpeedTesting = lazy(() => import("./pages/SpeedTesting"));
const CognitiveTesting = lazy(() => import("./pages/CognitiveTesting"));
const BodyComposition = lazy(() => import("./pages/BodyComposition"));
const WellnessTracking = lazy(() => import("./pages/WellnessTracking"));
const Performance = lazy(() => import("./pages/Performance"));
const StrengthConditioning = lazy(() => import("./pages/StrengthConditioning"));
const SCSessionBuilder = lazy(() => import("./components/sc/SessionBuilder"));
const SCSessionLogView = lazy(() => import("./components/sc/SessionLogView"));
const SCSessionRunView = lazy(() => import("./components/sc/SessionRunView"));
// Insights legacy page removed from routing — redirected to /app/insights-hub
const PlayerDetail = lazy(() => import("./pages/PlayerDetail"));
const DataValidation = lazy(() => import("./pages/DataValidation"));
const FitnessTesting = lazy(() => import("./pages/FitnessTesting"));
const MetabolicTesting = lazy(() => import("./pages/MetabolicTesting"));
const InjuryTracking = lazy(() => import("./pages/InjuryTracking"));
const ReturnToPlay = lazy(() => import("./pages/ReturnToPlay"));
const Integrations = lazy(() => import("./pages/Integrations"));
const OnboardingStart = lazy(() => import("./pages/OnboardingStart"));
const OrgOnboarding = lazy(() => import("./pages/OrgOnboarding"));
const PlayerLayout = lazy(() => import("./pages/player/PlayerLayout"));
const PlayerHome = lazy(() => import("./pages/player/PlayerHome"));
const CheckIn = lazy(() => import("./pages/player/CheckIn"));
const History = lazy(() => import("./pages/player/History"));
const Schedule = lazy(() => import("./pages/player/Schedule"));
const PlayerActions = lazy(() => import("./pages/player/PlayerActions"));
const MyClub = lazy(() => import("./pages/player/MyClub"));
const MyProfile = lazy(() => import("./pages/player/MyProfile"));
const ReportConcern = lazy(() => import("./pages/player/ReportConcern"));

const CoachInsights = lazy(() => import("./pages/CoachInsights"));
const TrainingPreview = lazy(() => import("./pages/TrainingPreview"));
const InsightsHub = lazy(() => import("./pages/InsightsHub"));
const Planning = lazy(() => import("./pages/Planning"));
// RecruitmentOpposition removed from product scope

const Analytics = lazy(() => import("./pages/Analytics"));
const DataManagement = lazy(() => import("./pages/DataManagement"));
const Install = lazy(() => import("./pages/Install"));
const Matches = lazy(() => import("./pages/Matches"));
const MatchView = lazy(() => import("./pages/MatchView"));
const Planner = lazy(() => import("./pages/Planner"));
const ScenarioPlanner = lazy(() => import("./pages/ScenarioPlanner"));
const Sessions = lazy(() => import("./pages/Sessions"));
const PlannedSessions = lazy(() => import("./pages/PlannedSessions"));
const PlannedSessionDetail = lazy(() => import("./pages/PlannedSessionDetail"));
const Travel = lazy(() => import("./pages/Travel"));
// DataSharing removed from product scope
// Scouting / Opposition pages removed from product scope
const ComparePlayer = lazy(() => import("./pages/ComparePlayer"));
const SquadGaps = lazy(() => import("./pages/SquadGaps"));
// AIReports removed from product scope
const SalesSheet = lazy(() => import("./pages/SalesSheet"));
const ClubCalendar = lazy(() => import("./pages/ClubCalendar"));
const RiskForecastPage = lazy(() => import("./pages/RiskForecast"));
const TestingCalendar = lazy(() => import("./pages/TestingCalendar"));
const BlockAnalyzer = lazy(() => import("./pages/BlockAnalyzer"));

const Messaging = lazy(() => import("./pages/Messaging"));
const Availability = lazy(() => import("./pages/Availability"));
const WellnessSummary = lazy(() => import("./pages/WellnessSummary"));

const Actions = lazy(() => import("./pages/Actions"));
const Uploads = lazy(() => import("./pages/Uploads"));
const Members = lazy(() => import("./pages/Members"));
// SeasonRegistrationManagement / CampsClinicsManagement / CampRegistrations /
// CampRegistrationPage / VideoAnalysis / Payments / ParentPayments /
// PaymentCheckout / PlayerPayment / Venues — all removed from product scope.
const ParentLayout = lazy(() => import("./pages/parent/ParentLayout"));
const ParentHome = lazy(() => import("./pages/parent/ParentHome"));
const ParentSchedule = lazy(() => import("./pages/parent/ParentSchedule"));
const ParentDocuments = lazy(() => import("./pages/parent/ParentDocuments"));
const ParentMessages = lazy(() => import("./pages/parent/ParentMessages"));
// ParentMedia removed from product scope
const ParentStaff = lazy(() => import("./pages/parent/ParentStaff"));
const ParentConsents = lazy(() => import("./pages/parent/ParentConsents"));
const StaffDirectory = lazy(() => import("./pages/StaffDirectory"));
// ClubMedia / Grants removed from product scope
const UnmatchedAthleteResolver = lazy(() => import("./pages/UnmatchedAthleteResolver"));
const MatchMode = lazy(() => import("./pages/MatchMode"));
const OrganizationSettings = lazy(() => import("./pages/settings/OrganizationSettings"));
const SquadManagement = lazy(() => import("./pages/settings/SquadManagement"));
const SecurityCompliance = lazy(() => import("./pages/settings/SecurityCompliance"));
const TeamManagement = lazy(() => import("./pages/settings/TeamManagement"));
const RolePermissions = lazy(() => import("./pages/settings/RolePermissions"));
const NotificationPreferences = lazy(() => import("./pages/settings/NotificationPreferences"));
const WorkflowDashboard = lazy(() => import("./pages/workflows/WorkflowDashboard"));
const WorkflowList = lazy(() => import("./pages/workflows/WorkflowList"));
const TemplateLibrary = lazy(() => import("./pages/workflows/TemplateLibrary"));
const WorkflowCanvas = lazy(() => import("./pages/workflows/WorkflowCanvas"));
const WorkflowHistory = lazy(() => import("./pages/workflows/WorkflowHistory"));
const AccountSettings = lazy(() => import("./pages/settings/AccountSettings"));
const KioskSettings = lazy(() => import("./pages/settings/KioskSettings"));
const KioskMode = lazy(() => import("./pages/kiosk/KioskMode"));
const BillingSettings = lazy(() => import("./pages/settings/BillingSettings"));
const DataExport = lazy(() => import("./pages/DataExport"));
const SuperAdminOrganizations = lazy(() => import("./pages/SuperAdminOrganizations"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Security = lazy(() => import("./pages/legal/Security"));
const GDPR = lazy(() => import("./pages/legal/GDPR"));
const DataRetention = lazy(() => import("./pages/legal/DataRetention"));
const DPA = lazy(() => import("./pages/legal/DPA"));
const Impressum = lazy(() => import("./pages/legal/Impressum"));
const Datenschutz = lazy(() => import("./pages/legal/Datenschutz"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
// Marketing page removed — /marketing now redirects to /
const RTPCaseDetail = lazy(() => import("./pages/RTPCaseDetail"));
const DirectorDashboard = lazy(() => import("./pages/DirectorDashboard"));
const OwnerDashboard = lazy(() => import("./pages/OwnerDashboard"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const Pricing = lazy(() => import("./pages/Pricing"));
// BriefingRoom and BriefingRoomEditor removed from product scope
const Compliance = lazy(() => import("./pages/Compliance"));
// Guardrails page removed — guardrail logic now lives in configurable workflow templates.

// Volunteers removed from product scope
// Finance dashboard removed — payments + billing + finance_entries table retained


const ReferralForm = lazy(() => import("./pages/ReferralForm"));
const DataImport = lazy(() => import("./pages/DataImport"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const ClubSetup = lazy(() => import("./pages/ClubSetup"));
// DrillLibrary removed from product scope
const Season = lazy(() => import("./pages/Season"));
const Terms = lazy(() => import("./pages/Terms"));

const Documents = lazy(() => import("./pages/Documents"));
const Enterprise = lazy(() => import("./pages/Enterprise"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ClubPublicPage = lazy(() => import("./pages/public/ClubPublicPage"));
// ClubRegistrationPage removed from product scope
// ClubInbox removed from product scope

// ── Section landing stubs (Phase 1 of section-based nav) ───────────────────
const SquadSection = lazy(() => import("./pages/sections/SquadSection"));
const MatchesSection = lazy(() => import("./pages/sections/MatchesSection"));
const TrainingSection = lazy(() => import("./pages/sections/TrainingSection"));
const ClubSection = lazy(() => import("./pages/sections/ClubSection"));
const BusinessSection = lazy(() => import("./pages/sections/BusinessSection"));
const AnalyticsSection = lazy(() => import("./pages/sections/AnalyticsSection"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,        // data stays fresh for 60 seconds
      retry: 1,                 // retry failed requests once, not three times
      refetchOnWindowFocus: false, // don't refetch every time the user switches tabs
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LocaleProvider>
      <TooltipProvider>
        <ErrorBoundary>
          <Sentry.ErrorBoundary fallback={({ error }) => <AppErrorFallback error={error} />}>
          <Toaster />
          <Sonner />
          <CookieConsentBanner />
          <BrowserRouter>
            <OrganizationProvider>
              <OnboardingProvider>
                <Suspense fallback={<LoadingScreen />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/marketing" element={<Navigate to={ROUTES.HOME} replace />} />
                    <Route path="/sales-sheet" element={<SalesSheet />} />
                    <Route path={ROUTES.AUTH} element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/get-started" element={<GetStarted />} />
                    <Route path="/install" element={<Install />} />
                    <Route path="/invite/:token" element={<InviteAccept />} />
                    <Route path={ROUTES.ONBOARDING_START} element={<OnboardingStart />} />
                    <Route path={ROUTES.ONBOARDING_ORG} element={<OrgOnboarding />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    <Route path={ROUTES.PRICING} element={<Pricing />} />
                    <Route path="/enterprise" element={<Enterprise />} />
                    <Route path="/clubs" element={<Navigate to={ROUTES.HOME} replace />} />
                    <Route path="/clubs/:slug" element={<ClubPublicPage />} />
                    {/* /clubs/:slug/join and /camps/:slug/:campId removed — registration features cut */}
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/kiosk/:orgSlug" element={<KioskMode />} />
                   <Route path="/owner" element={
                     <RouteGuard requiredRoles={["super_admin"]}>
                       <OwnerDashboard />
                     </RouteGuard>
                   } />
                    

                    {/* App routes with layout */}
                    <Route path="/app" element={<AppLayout />}>
                      {/* Section landing routes — Phase 1 stubs that redirect to the
                          current default page within each section. Existing routes are
                          preserved below so all 83 internal nav callers keep working. */}
                      <Route path="squad" element={<Navigate to={ROUTES.PLAYERS} replace />} />
                      <Route path="training" element={<TrainingSection />} />
                      <Route path="club" element={<ClubSection />} />
                      <Route path="business" element={<BusinessSection />} />
                      <Route path="analytics" element={<AnalyticsSection />} />
                      <Route path="admin" element={<Navigate to={ROUTES.SETTINGS} replace />} />
                      {/* NOTE: /app/matches is NOT aliased — it already resolves to the
                          existing Matches page, which is also the default for the
                          Matches section. Same for any /app/<key> that already exists. */}

                      <Route path="club-setup" element={<ClubSetup />} />
                      <Route path="dashboard" element={<Navigate to={ROUTES.WORKFLOWS} replace />} />
                      <Route path="season" element={<Season />} />
                      <Route path="players" element={<Players />} />
                      <Route path="players/:id" element={<PlayerDetail />} />
                      <Route path="import" element={<Navigate to={ROUTES.UPLOADS} replace />} />
                      <Route path="insights" element={<Navigate to={ROUTES.INSIGHTS_HUB} replace />} />
                      <Route path="performance" element={
                        <RouteGuard requiredRoles={["org_admin", "performance", "medical", "coach"]}>
                          <FeatureErrorBoundary featureName="Performance Hub">
                            <Performance />
                          </FeatureErrorBoundary>
                        </RouteGuard>
                      } />
                      <Route path="gps" element={<Navigate to={ROUTES.LOAD_MONITORING} replace />} />
                      <Route path="load-monitoring" element={
                        <FeatureRouteGuard feature="gps_analytics">
                          <FeatureErrorBoundary featureName="Load & Monitoring">
                            <LoadMonitoring />
                          </FeatureErrorBoundary>
                        </FeatureRouteGuard>
                      } />

                      <Route path="speed" element={<Navigate to={`${ROUTES.PERFORMANCE}?tab=speed`} replace />} />
                      <Route path="cognitive" element={<Navigate to={`${ROUTES.PERFORMANCE}?tab=cognitive`} replace />} />
                      <Route path="bodycomp" element={<Navigate to="/app/performance?tab=bodycomp" replace />} />
                      <Route path="fitness" element={<Navigate to={`${ROUTES.PERFORMANCE}?tab=strength`} replace />} />
                      <Route path="metabolic" element={<Navigate to="/app/performance?tab=metabolic" replace />} />
                      <Route path="strength-conditioning" element={
                        <FeatureRouteGuard feature="strength_conditioning">
                          <FeatureErrorBoundary featureName="Strength & Conditioning">
                            <StrengthConditioning />
                          </FeatureErrorBoundary>
                        </FeatureRouteGuard>
                      } />
                      <Route path="strength-conditioning/session/:sessionId" element={
                        <RouteGuard requiredRoles={["org_admin", "coach", "performance", "super_admin"]}>
                          <FeatureRouteGuard feature="strength_conditioning">
                            <FeatureErrorBoundary featureName="Session Builder">
                              <SCSessionBuilder />
                            </FeatureErrorBoundary>
                          </FeatureRouteGuard>
                        </RouteGuard>
                      } />
                      <Route path="strength-conditioning/session/:sessionId/run" element={
                        <RouteGuard requiredRoles={["org_admin", "coach", "performance", "super_admin"]}>
                          <FeatureRouteGuard feature="strength_conditioning">
                            <FeatureErrorBoundary featureName="Session Run View">
                              <SCSessionRunView />
                            </FeatureErrorBoundary>
                          </FeatureRouteGuard>
                        </RouteGuard>
                      } />
                      <Route path="strength-conditioning/session/:sessionId/log" element={
                        <FeatureRouteGuard feature="strength_conditioning">
                          <FeatureErrorBoundary featureName="Session Log">
                            <SCSessionLogView />
                          </FeatureErrorBoundary>
                        </FeatureRouteGuard>
                      } />
                     <Route path="wellness" element={
                       <FeatureRouteGuard feature="wellness_tracking">
                         <FeatureErrorBoundary featureName="Wellness">
                           <WellnessTracking />
                         </FeatureErrorBoundary>
                       </FeatureRouteGuard>
                     } />
                      <Route path="injuries" element={
                        <FeatureErrorBoundary featureName="Injury Tracking">
                          <InjuryTracking />
                        </FeatureErrorBoundary>
                      } />
                      <Route path="validation" element={<DataValidation />} />
                     <Route path="upload-history" element={
                       <FeatureRouteGuard feature="csv_uploads">
                         <UploadHistory />
                       </FeatureRouteGuard>
                     } />
                      <Route
                        path="integrations"
                        element={
                          <RouteGuard requiredRoles={["org_admin", "performance", "medical", "coach"]}>
                            <Integrations />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="user-management"
                        element={
                          <RouteGuard requiredRoles={["org_admin"]}>
                            <TeamManagement />
                          </RouteGuard>
                        }
                      />
                      
                      <Route path="coach-insights" element={<Navigate to="/app/insights-hub?tab=coach-briefing" replace />} />
                     <Route path="insights-hub" element={
                       <FeatureRouteGuard feature="ai_insights_hub">
                         <FeatureErrorBoundary featureName="Insights Hub">
                           <InsightsHub />
                         </FeatureErrorBoundary>
                       </FeatureRouteGuard>
                     } />
                      <Route path="planning" element={
                        <RouteGuard requiredRoles={["org_admin", "performance", "medical", "coach"]}>
                          <FeatureErrorBoundary featureName="Planning">
                            <Planning />
                          </FeatureErrorBoundary>
                        </RouteGuard>
                      } />
                      {/* /app/recruitment-opposition removed — feature cut from product scope */}
                      <Route path="training-preview" element={<Navigate to="/app/sessions?tab=preview" replace />} />
                     <Route path="analytics" element={
                       <FeatureRouteGuard feature="gps_analytics">
                         <FeatureErrorBoundary featureName="Analytics">
                           <Analytics />
                         </FeatureErrorBoundary>
                       </FeatureRouteGuard>
                     } />
                      <Route path="data-management" element={<DataManagement />} />
                      <Route
                        path="matches"
                        element={
                          <FeatureRouteGuard feature="session_planner">
                            <FeatureErrorBoundary featureName="Matches">
                              <Matches />
                            </FeatureErrorBoundary>
                          </FeatureRouteGuard>
                        }
                      />
                      <Route
                        path="matches/:matchId"
                        element={
                          <FeatureRouteGuard feature="basic_match_scheduling">
                            <FeatureErrorBoundary featureName="Match View">
                              <MatchView />
                            </FeatureErrorBoundary>
                          </FeatureRouteGuard>
                        }
                      />
                      <Route
                        path="match"
                        element={
                          <FeatureRouteGuard feature="match_congestion_management">
                            <FeatureErrorBoundary featureName="Match Mode">
                              <MatchMode />
                            </FeatureErrorBoundary>
                          </FeatureRouteGuard>
                        }
                      />
                      <Route
                        path="sessions"
                        element={
                          <FeatureRouteGuard feature="session_planner">
                            <Sessions />
                          </FeatureRouteGuard>
                        }
                      />
                      <Route
                        path="planner"
                        element={<Navigate to={`${ROUTES.SESSIONS}?tab=planner`} replace />}
                      />
                      <Route
                        path="planned-sessions"
                        element={<Navigate to={`${ROUTES.SESSIONS}?tab=schedule`} replace />}
                      />
                      <Route path="scenario-planner" element={<Navigate to="/app/planning?tab=scenario-planner" replace />} />
                      <Route
                        path="planned-sessions/:sessionId"
                        element={
                          <RouteGuard requiredRoles={["org_admin", "performance", "coach", "medical"]}>
                            <FeatureRouteGuard feature="session_planner">
                              <PlannedSessionDetail />
                            </FeatureRouteGuard>
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="travel"
                        element={
                          <FeatureRouteGuard feature="travel_jet_lag">
                            <RouteGuard requiredRoles={["org_admin", "performance", "medical", "coach"]}>
                              <Travel />
                            </RouteGuard>
                          </FeatureRouteGuard>
                        }
                      />
                      {/* /app/data-sharing removed — feature cut from product scope */}
                      {/* /app/scouting and /app/scouting/:id removed — feature cut from product scope */}
                      <Route
                        path="compare"
                        element={
                          <FeatureRouteGuard feature="compare_players">
                            <RouteGuard requiredRoles={["org_admin", "performance", "coach", "medical"]}>
                              <ComparePlayer />
                            </RouteGuard>
                          </FeatureRouteGuard>
                        }
                      />
                      <Route
                        path="squad-gaps"
                        element={
                          <RouteGuard requiredRoles={["org_admin", "performance", "coach"]}>
                            <FeatureRouteGuard feature="squad_gap_analysis">
                              <SquadGaps />
                            </FeatureRouteGuard>
                          </RouteGuard>
                        }
                      />
                      <Route path="ai-reports" element={<Navigate to="/app/insights-hub?tab=reports" replace />} />
                      <Route
                        path="rtp"
                        element={
                          <RouteGuard requiredRoles={["org_admin", "performance", "medical", "coach"]}>
                            <FeatureRouteGuard feature="rtp_workflows">
                              <ReturnToPlay />
                            </FeatureRouteGuard>
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="rtp/:id"
                        element={
                          <RouteGuard requiredRoles={["org_admin", "performance", "medical", "coach"]}>
                            <FeatureRouteGuard feature="rtp_workflows">
                              <RTPCaseDetail />
                            </FeatureRouteGuard>
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="director-dashboard"
                        element={
                          <RouteGuard requiredRoles={["org_admin"]}>
                            <DirectorDashboard />
                          </RouteGuard>
                        }
                      />
                      <Route path="risk-forecast" element={<Navigate to="/app/planning?tab=risk-forecast" replace />} />
                      <Route path="weekly-report" element={<Navigate to="/app/ai-reports?tab=weekly" replace />} />
                      <Route path="testing-calendar" element={<TestingCalendar />} />
                     <Route path="block-analyzer" element={
                       <FeatureRouteGuard feature="gps_analytics">
                         <BlockAnalyzer />
                       </FeatureRouteGuard>
                     } />
                      <Route path="availability" element={
                        <FeatureRouteGuard feature="availability_tracking">
                          <Availability />
                        </FeatureRouteGuard>
                      } />
                      <Route path="wellness-summary" element={
                        <FeatureRouteGuard feature="basic_wellness_tracking">
                          <WellnessSummary />
                        </FeatureRouteGuard>
                      } />
                      <Route path="messaging" element={<Messaging />} />{/* staff messaging — /app/messaging */}
                      <Route path="calendar" element={<ClubCalendar />} />
                       
                       {/* drill-library, season-registration, camps-clinics, camp-registrations,
                           venues, video-analysis, payments, opposition-analysis — all removed
                           from product scope. */}
                       <Route path="documents" element={<Documents />} />
                       <Route path="actions" element={<Actions />} />
                       <Route path="members" element={<Members />} />
                       <Route path="return-to-play" element={<Navigate to={ROUTES.RTP} replace />} />
                       {/* briefing-room routes retired — feature removed */}
                      <Route
                        path="compliance"
                        element={
                          <RouteGuard requiredRoles={["org_admin", "performance", "medical", "coach"]}>
                            <Compliance />
                          </RouteGuard>
                        }
                      />
                      {/* Guardrails route removed — see workflow templates instead. */}
                      {/* /app/volunteers and /app/media removed — features cut from product scope */}
                      <Route path="staff-directory" element={
                        <RouteGuard requiredRoles={["org_admin", "super_admin"]}>
                          <StaffDirectory />
                        </RouteGuard>
                      } />
                      <Route path="knowledge-base" element={<Navigate to={ROUTES.DOCUMENTS} replace />} />
                     <Route path="data-import" element={
                       <FeatureRouteGuard feature="historical_import">
                         <DataImport />
                       </FeatureRouteGuard>
                     } />
                      <Route
                        path="uploads"
                        element={
                          <FeatureErrorBoundary featureName="Uploads">
                            <Uploads />
                          </FeatureErrorBoundary>
                        }
                      />
                      <Route path="uploads/:uploadId/resolve" element={<UnmatchedAthleteResolver />} />
                      <Route path="pricing" element={<Navigate to={ROUTES.PRICING} replace />} />
                      <Route path="settings" element={<Settings />} />
                      <Route path="settings/account" element={<AccountSettings />} />
                      <Route
                        path="settings/billing"
                        element={
                          <RouteGuard requiredRoles={["org_admin"]}>
                            <BillingSettings />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="settings/organization"
                        element={
                          <RouteGuard requiredRoles={["org_admin", "performance", "medical", "coach"]}>
                            <OrganizationSettings />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="settings/team"
                        element={
                          <RouteGuard requiredRoles={["org_admin"]}>
                            <TeamManagement />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="settings/role-permissions"
                        element={
                          <RouteGuard requiredRoles={["org_admin"]}>
                            <RolePermissions />
                          </RouteGuard>
                        }
                      />

                      <Route
                        path="settings/squads"
                        element={
                          <RouteGuard requiredRoles={["org_admin"]}>
                            <SquadManagement />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="settings/security-and-compliance"
                        element={
                          <RouteGuard requiredRoles={["org_admin", "performance"]}>
                            <SecurityCompliance />
                          </RouteGuard>
                        }
                      />
                      <Route
                        path="settings/data-export"
                        element={
                          <RouteGuard requiredRoles={["org_admin", "super_admin"]}>
                            <DataExport />
                          </RouteGuard>
                        }
                      />
                      <Route path="settings/notifications" element={<NotificationPreferences />} />
                      <Route path="settings/kiosk" element={
                        <RouteGuard requiredRoles={["org_admin", "performance", "medical", "coach", "super_admin"]}>
                          <KioskSettings />
                        </RouteGuard>
                      } />
                      <Route path="workflows" element={
                        <RouteGuard requiredRoles={["org_admin", "super_admin"]}>
                          <WorkflowDashboard />
                        </RouteGuard>
                      } />
                      <Route path="workflows/all" element={
                        <RouteGuard requiredRoles={["org_admin", "super_admin"]}>
                          <WorkflowList />
                        </RouteGuard>
                      } />
                      <Route path="workflows/templates" element={
                        <RouteGuard requiredRoles={["org_admin", "super_admin"]}>
                          <TemplateLibrary />
                        </RouteGuard>
                      } />
                      <Route path="workflows/new" element={
                        <RouteGuard requiredRoles={["org_admin", "super_admin"]}>
                          <WorkflowCanvas />
                        </RouteGuard>
                      } />
                      <Route path="workflows/:id" element={
                        <RouteGuard requiredRoles={["org_admin", "super_admin"]}>
                          <WorkflowCanvas />
                        </RouteGuard>
                      } />
                      <Route path="workflows/:id/history" element={
                        <RouteGuard requiredRoles={["org_admin", "super_admin", "coach", "performance", "medical"]}>
                          <WorkflowHistory />
                        </RouteGuard>
                      } />
                     <Route path="super-admin/organizations" element={
                       <RouteGuard requiredRoles={["super_admin"]}>
                         <SuperAdminOrganizations />
                       </RouteGuard>
                     } />
                    </Route>

                    {/* Legal & Compliance Routes */}
                    <Route path="/legal/privacy" element={<Privacy />} />
                    <Route path="/legal/security" element={<Security />} />
                    <Route path="/legal/gdpr" element={<GDPR />} />
                    <Route path="/legal/data-retention" element={<DataRetention />} />
                    <Route path="/legal/dpa" element={<DPA />} />
                    <Route path="/legal/impressum" element={<Impressum />} />
                    <Route path="/legal/datenschutz" element={<Datenschutz />} />
                    <Route path="/legal/terms" element={<TermsOfService />} />

                    {/* Player Wellness Portal */}
                    <Route path="/player" element={<PlayerLayout />}>
                      <Route index element={<PlayerHome />} />
                      <Route path="home" element={<PlayerHome />} />
                      <Route path="check-in" element={<CheckIn />} />
                      <Route path="history" element={<History />} />
                      <Route path="schedule" element={<Schedule />} />
                      <Route path="player-actions" element={<PlayerActions />} />
                      <Route path="my-club" element={<MyClub />} />
                      <Route path="profile" element={<MyProfile />} />
                      <Route path="report-concern" element={<ReportConcern />} />
                      <Route path="messages" element={<Messaging />} />
                    </Route>

                     {/* External clinic referral form — public, no auth */}
                     <Route path="/referral/:token" element={<ReferralForm />} />


                     {/* Parent Portal - standalone, with bottom nav */}
                    <Route path={ROUTES.PARENT_PORTAL} element={<ParentLayout />}>
                      <Route index element={<ParentHome />} />
                      <Route path="home" element={<ParentHome />} />
                      <Route path="schedule" element={<ParentSchedule />} />
                      {/* parent payments removed — feature cut from product scope */}
                      <Route path="documents" element={<ParentDocuments />} />
                      <Route path="messages" element={<ParentMessages />} />
                      {/* /app/parent-portal/media removed — feature cut from product scope */}
                      <Route path="staff" element={<ParentStaff />} />
                      <Route path="consents" element={<ParentConsents />} />
                    </Route>

                    {/* Payment Checkout & player-facing payment removed — feature cut from product scope */}

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </OnboardingProvider>
            </OrganizationProvider>
          </BrowserRouter>
        </Sentry.ErrorBoundary>
        </ErrorBoundary>
      </TooltipProvider>
    </LocaleProvider>
  </QueryClientProvider>
);

export default App;
